import type { ReactNode } from "react";
import { SuitGlyph } from "../components/SuitGlyph";
import { fa } from "../i18n/fa";

/* ------------------------------------------------------------------ *
 * THE TRUMP SIGN — حکم's «حکم:» chip on its felt's top row (`HokmTable`, at the row's end beside the
 * trick counter) until card-67 B2 (2026-10-22), when شلم's trump became its second user (the
 * operator: «Trump sign: top left, exactly like حکم») and it moved here byte for byte, the «حاکم»
 * chip's way (`hakem-chip.tsx`): `trump-sign-move.test.tsx` pins the markup HokmTable drew before the
 * move. Each table decides WHERE it stands — حکم's top row (`inset-x-3 top-6`, at its end: the
 * felt's top-left under rtl), شلم's top-left corner (`left-3 top-6`: the same place) — and nothing
 * else.
 * ------------------------------------------------------------------ */

/** The red suits: their pip wears red on the gold, the black ones the gold's ink. */
const RED_SUITS: ReadonlySet<string> = new Set(["hearts", "diamonds"]);

export function TrumpSign({
  suit,
  modeLabel,
}: {
  /** The trump's suit — the engine-core long form (`"spades"`…), drawn as its pip on the gold disc. */
  suit: string | null;
  /** One of حکم's suit-less calls («سرس» / «نرس» / «آس نرس»): it has no pip, so its NAME sits in the
   *  disc's gold slot instead. */
  modeLabel?: string | undefined;
}): ReactNode {
  return (
    <div className="flex items-center gap-2 rounded-full border border-secondary/40 bg-surface-container/85 px-2.5 py-1 shadow-lg">
      <span className="m-0 font-caption text-caption text-secondary">{`${fa.hokm.trump}:`}</span>
      {modeLabel === undefined ? (
        <span
          className="grid place-items-center rounded-full bg-secondary"
          style={{ width: 26, height: 26, overflow: "visible" }}
        >
          <span
            aria-hidden
            style={{
              fontSize: 22,
              lineHeight: 1,
              transform: "translateY(0.5px)",
              color: RED_SUITS.has(suit ?? "")
                ? "rgb(var(--c-suit-red-on-gold))"
                : "rgb(var(--c-ink-on-gold))",
            }}
          >
            <SuitGlyph suit={suit} />
          </span>
        </span>
      ) : (
        /* A suit-less call — the NAME sits in the pip's gold slot. Same
           26px height as the disc, so the row's geometry does not move.
           ⚠️ 12px + px-1.5 is MEASURED, not taste. This row shares its band
           with the partner's SEAT AVATAR (centred on the felt, y 86..130 against
           the chip's y 90..124; the seat's NAME sits lower at y 136 and never
           collides). At 320px (iPhone SE) that avatar's left edge is x=138, and
           the chip starts at x=24, so what matters is the chip's RIGHT EDGE:

             call      right edge @14px/px-2   @12px/px-1.5   clearance to x=138
             spades          103.0                103.0            35.0   (a pip
                                                                    disc — the
                                                                    type scale
                                                                    cannot move it)
             نرس             119.3                110.8            27.2
             سرس             126.3                116.9            21.1
             آس نرس          144.1                131.4    −6.1 ✗ →  +6.6 ✓

           So the widest name OVERLAPPED the avatar by 6.1px at 14px/px-2 and
           clears it by 6.6px here. (The chip's WIDTH is 120.1px → 107.4px; the
           numbers above are POSITIONS, which is what decides the collision.)
           Re-measured in a real browser 2026-08-25; the earlier note said the
           avatar started at x=140 and quoted the widths and positions
           interchangeably. Pinned by `hokm-trump-modes.test.tsx`. (Moved here
           from HokmTable with the chip, card-67 B2.) */
        <span
          className="grid place-items-center rounded-full bg-secondary px-1.5 font-caption text-caption font-bold whitespace-nowrap"
          style={{ height: 26, color: "rgb(var(--c-ink-on-gold))" }}
        >
          {modeLabel}
        </span>
      )}
    </div>
  );
}
